import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  Box,
  Toolbar,
  Divider,
  Menu,
  MenuItem,
  useMediaQuery,
  Container,
  useTheme,
  type Theme,
  ListItemIcon,
  ListItemText,
  alpha,
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  Lock as LockIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  GetApp as GetAppIcon,
} from '@mui/icons-material';

import { useAuth } from '../contexts/AuthContext';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';
import { usePWA } from '../hooks/usePWA';
import MobileFooterMenu from './MobileFooterMenu';
import { MOBILE_FOOTER_CONTENT_PB } from './mobileFooterMenuStyles';
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import { InactivityLockMonitor } from '../components/common';
import PwaInstallModal from '../components/modals/PwaInstallModal';
import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_COLLAPSED, MOBILE_APP_BAR_HEIGHT, DESKTOP_APP_BAR_HEIGHT } from './sidebarStyles';

const drawerWidth = SIDEBAR_WIDTH;
const drawerWidthCollapsed = SIDEBAR_WIDTH_COLLAPSED;

// Estilos do Toolbar
const toolbarStyles = {
  offset: {
    minHeight: {
      xs: MOBILE_APP_BAR_HEIGHT,
      sm: DESKTOP_APP_BAR_HEIGHT,
    },
  },
};

// Estilos dos Menus
const menuStyles = {
  profile: (isMobile: boolean, isDarkMode: boolean, theme: Theme) => ({
    elevation: 0,
    overflow: 'visible',
    filter: isDarkMode
      ? 'drop-shadow(0px 8px 24px rgba(0,0,0,0.55))'
      : 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
    mt: 1.5,
    minWidth: isMobile ? 200 : 180,
    ...(isDarkMode && {
      bgcolor: theme.palette.background.paper,
      border: `1px solid ${alpha('#3b82f6', 0.18)}`,
      backgroundImage: 'none',
    }),
    '& .MuiAvatar-root': {
      width: 32,
      height: 32,
      ml: -0.5,
      mr: 1,
    },
    '& .MuiMenuItem-root': {
      minHeight: isMobile ? 48 : 40,
      fontSize: isMobile ? '1rem' : '0.875rem',
    },
  }),
};

// Estilos do Container Principal
const mainContainerStyles = {
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column' as const,
  minHeight: '100vh',
};

// Estilos do Container de Conteúdo
const contentContainerStyles = {
  flexGrow: 1,
  py: {
    xs: 2, // Mobile: padding menor
    sm: 1, // Desktop: padding padrão
  },
  px: {
    xs: 1, // Mobile: padding lateral mínimo
    sm: 1, // Tablet: padding reduzido
    md: 2, // Desktop: padding reduzido
  },
  pb: {
    xs: MOBILE_FOOTER_CONTENT_PB,
    md: 2,
  },
};

const Layout: React.FC = () => {
  const theme = useTheme();
  const { isDarkMode } = useAppTheme();
  const navigate = useNavigate();
  const { logout, lockSystem } = useAuth();
  
  // Breakpoints mais precisos para melhor responsividade
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600-900px
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg')); // >= 1200px

  // Inicializar estado do sidebar com localStorage
  const [open, setOpen] = useState(() => {
    // Em mobile/tablet, sempre começa fechado
    if (isMobile || isTablet) {
      return false;
    }
    
    // Em desktop, verificar localStorage ou usar padrão (aberto)
    const savedState = localStorage.getItem('sidebarOpen');
    if (savedState !== null) {
      return savedState === 'true';
    }
    return isDesktop; // Aberto por padrão em desktop
  });
  
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Ajustar drawer baseado no tamanho da tela com lógica melhorada
  React.useEffect(() => {
    if (isMobile || isTablet) {
      setOpen(false); // Sempre fechado em mobile/tablet
    } else if (isDesktop) {
      // Em desktop, verificar localStorage
      const savedState = localStorage.getItem('sidebarOpen');
      if (savedState !== null) {
        setOpen(savedState === 'true');
      } else {
        setOpen(true); // Aberto por padrão em desktop se não houver preferência
      }
    }
  }, [isMobile, isTablet, isDesktop]);

  const handleDrawerToggle = () => {
    const newState = !open;
    setOpen(newState);
    
    // Salvar preferência apenas em desktop
    if (!isMobile && !isTablet) {
      localStorage.setItem('sidebarOpen', String(newState));
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await logout();
    navigate('/login');
  };

  const { isInstalled, isInstallable, promptInstall } = usePWA();
  const [pwaModalOpen, setPwaModalOpen] = useState(false);

  const handleOpenPwaModal = async () => {
    handleProfileMenuClose();
    if (isInstallable) {
      const res = await promptInstall();
      if (res === 'manual_instructions' || res === 'already_installed') {
        setPwaModalOpen(true);
      }
    } else {
      setPwaModalOpen(true);
    }
  };

  const handleLockSystem = () => {
    handleProfileMenuClose();
    lockSystem();
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar
        open={open}
        setOpen={(newState) => {
          setOpen(newState);
          if (!isMobile && !isTablet) {
            localStorage.setItem('sidebarOpen', String(newState));
          }
        }}
        onHoverChange={setSidebarHovered}
      />

      <Header
        onDrawerToggle={handleDrawerToggle}
        onProfileMenuOpen={handleProfileMenuOpen}
        sidebarOpen={open}
        sidebarHovered={sidebarHovered}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          ...mainContainerStyles,
          flexGrow: 1,
          width: {
            xs: '100%', // Mobile: largura total
            sm: `calc(100% - ${isMobile ? 0 : ((open || sidebarHovered) ? drawerWidth : drawerWidthCollapsed)}px)`,
          },
          transition: theme.transitions.create(['width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar sx={toolbarStyles.offset} />
        
        {/* Content Area */}
        <Container
          maxWidth={false}
          sx={{
            ...contentContainerStyles,
            pl: {
              xs: 1, // Mobile: padding left normal
              sm: 0.5, // Desktop: padding left mínimo para evitar espaço com sidebar
              md: 1, // Desktop: padding left reduzido
            },
          }}
        >
          <Outlet />
        </Container>

        {/* Footer */}
        <Footer />
      </Box>

      {/* Mobile Footer Menu */}
      <MobileFooterMenu />

      <InactivityLockMonitor onLock={lockSystem} />

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        PaperProps={{
          elevation: 0,
          sx: menuStyles.profile(isMobile, isDarkMode, theme),
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => navigate('/profile')}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Meu Perfil</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => navigate('/settings')}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Configurações</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleLockSystem}>
          <ListItemIcon>
            <LockIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Bloquear</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenPwaModal}>
          <ListItemIcon>
            <GetAppIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Instalar App</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sair</ListItemText>
        </MenuItem>
      </Menu>

      <PwaInstallModal
        open={pwaModalOpen}
        onClose={() => setPwaModalOpen(false)}
        isInstallable={isInstallable}
        isInstalled={isInstalled}
        onInstall={promptInstall}
      />

    </Box>
  );
};

export default Layout; 
